<template>
    <v-row no-gutters class="mt-5 px-8">
        <v-col cols="6" class="pr-8">
            <v-card outlined v-if="couponWithTransaction.goal_type == 'visits'">
                <v-card-title
                    class="subtitle-1 font-weight-bold"
                    v-text="translations.yourSettings[lang]"
                ></v-card-title>

                <v-list subheader dense>
                    <v-list-item
                        v-for="setting in settings"
                        :key="setting.text['en']"
                    >
                        <v-list-item-icon class="mr-3">
                            <v-icon
                                class="text--primary"
                                v-text="setting.icon"
                            ></v-icon>
                        </v-list-item-icon>
                        <v-list-item-title
                            style="white-space: normal"
                            v-text="setting.text[lang]"
                        ></v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-card>

            <v-card outlined v-else>
                <v-card-title
                    class="subtitle-1 font-weight-bold"
                    v-text="translations.yourSettings[lang]"
                ></v-card-title>

                <v-list subheader dense>
                    <v-list-item>
                        <v-list-item-icon class="mr-3">
                            <v-icon
                                class="text--primary"
                                v-text="icons.mdiStoreOutline"
                            ></v-icon>
                        </v-list-item-icon>
                        <v-list-item-title
                            style="white-space: normal"
                            v-text="settingsDescription[lang]"
                        ></v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-card>

            <v-card outlined class="mt-5">
                <v-card-title
                    class="subtitle-1 font-weight-bold"
                    v-text="translations.reward[lang]"
                ></v-card-title>

                <v-row no-gutters align="center" class="pa-4 pt-0 subtitle-2">
                    <v-col cols="auto">
                        <v-img
                            :src="couponWithTransaction.image"
                            width="60"
                            height="50"
                            contain
                        ></v-img>
                    </v-col>

                    <v-col class="pl-3"
                        >{{ couponWithTransaction.gift_title }}
                    </v-col>

                    <v-col cols="4" class="text-center">
                        <p>
                            <b>{{ translations.claimedCoupons[lang] }}</b>
                        </p>
                        <p>
                            <b>{{ translations.couponValidUntil[lang] }}</b>
                        </p>
                    </v-col>
                    <v-col cols="2" class="text-center">
                        <p>
                            {{
                                couponWithTransaction.maximum -
                                couponWithTransaction.available
                            }}
                            /
                            {{ couponWithTransaction.maximum }}
                        </p>
                        <p v-if="couponWithTransaction.expiration_date">
                            {{
                                moment(
                                    String(
                                        couponWithTransaction.expiration_date
                                    )
                                ).format("YYYY-MM-DD")
                            }}
                        </p>
                        <p v-else>{{ translations.doesNotExpire[lang] }}</p>
                    </v-col>
                </v-row>
            </v-card>

            <div class="text-right mt-4">
                <v-btn
                    :color="
                        superUser || permissions.delete
                            ? 'red accent-4'
                            : 'grey'
                    "
                    class="text-capitalize white--text px-10"
                    style="font-size: 0.9rem"
                    depressed
                    v-text="translations.delete[lang]"
                    @click="
                        () => {
                            if (superUser || permissions.delete) {
                                deleteDialog = true;
                            } else {
                                setPermissionDialog(true);
                            }
                        }
                    "
                ></v-btn>
            </div>
        </v-col>

        <v-col
            cols="6"
            class="pl-8"
            v-if="couponWithTransaction.goal_type == 'visits'"
        >
            <v-card flat>
                <v-card-title
                    class="subtitle-1 font-weight-bold pa-0"
                    v-text="translations.customersFollowing[lang]"
                ></v-card-title>
                <hr />

                <div class="mt-4" style="font-size: 0.875rem">
                    <b-data-table
                        v-if="users.length"
                        :headers="headers"
                        :items="users"
                        :page.sync="page"
                        @page-sync="p=>page=p"
                        disable-sort
                        class="b-outlined"
                    >
                        <template v-slot:item.totalVisits="{ item }">
                            {{ item.goal_counter }} /
                            {{ item.goal_sequence }}
                        </template>
                        <template v-slot:item.status="{ item }">
                            <span v-if="item.status === 'Claimed'">{{
                                translations.claimed[lang]
                            }}</span>
                            <span v-if="item.status === 'Redeemed'">{{
                                translations.redeemed[lang]
                            }}</span>
                            <span v-if="item.status === 'In Progress'">{{
                                translations.inProgress[lang]
                            }}</span>
                        </template>
                    </b-data-table>

                    <span v-else v-text="translations.noCustomers[lang]"></span>
                </div>
            </v-card>
        </v-col>

        <v-dialog v-model="deleteDialog" max-width="500">
            <b-card
                :disabled="!getPermission('coupons.goal.delete')"
                type="delete"
                :title="translations.deleteCoupon[lang]"
                :loading="loading"
                :error-message="errorMessage"
                :submit-text="{ el: '', en: 'delete', it: '' }"
                @cancel="deleteDialog = false"
                @submit="remove"
            >
                <div
                    class="subtitle-1 font-weight-medium py-3 px-2"
                    v-text="translations.areYouSure[lang]"
                ></div>
            </b-card>
        </v-dialog>
    </v-row>
</template>

<script>
import { mdiStoreOutline, mdiCashMultiple, mdiClockOutline } from "@mdi/js";
import { mapState, mapMutations, mapActions } from "vuex";
import translations from "@/utils/translations/storePanel/coupons/couponsWithTransactions/couponWithTransaction";
import moment from "moment";

export default {
    name: "CouponWithTransaction",

    mixins: [translations],

    data() {
        return {
            page: 1,
            moment: moment,
            icons: {
                mdiStoreOutline,
            },
        };
    },

    computed: {
        ...mapState([
            "loading",
            "errorMessage",
            "serverItemsLength",
            "superUser",
        ]),
        ...mapState("storePanel/coupons/couponsWithTransactions", [
            "couponWithTransaction",
            "users",
        ]),

        lang() {
            return this.$route.params.lang;
        },

        permissions() {
            return this.$store.state.permissions.coupons
                ? this.$store.state.permissions.coupons.goal
                : {};
        },

        deleteDialog: {
            get() {
                return this.$store.state.deleteDialog;
            },

            set(val) {
                this.setDeleteDialog(val);
            },
        },

        settingsDescription() {
            return {
                el: `Οι πελάτες πρέπει να πραγματοποιήσουν συναλλαγές συνολικής αξίας ${this.couponWithTransaction.goal_sum_sequence} ευρώ, για να κερδίσουν αυτήν την επιβράευση`,
                en: `Customers need to make transactions of ${this.couponWithTransaction.goal_sum_sequence} total spending in order to get that reward`,
                it: `I clienti devono fare transazioni di ${this.couponWithTransaction.goal_sum_sequence} spese totali per ottenere questa ricompensa`,
            };
        },

        settings() {
            return [
                {
                    icon: mdiStoreOutline,
                    text: {
                        el: `Ο πελάτης πρέπει να επισκεφθεί ${this.couponWithTransaction.goal_sequence} φορές το κατάστημά σου`,
                        en: `Customer must visit your store ${this.couponWithTransaction.goal_sequence} times`,
                        it: `Il cliente deve venire ${this.couponWithTransaction.goal_sequence} volte nel tuo negozio`,
                    },
                },
                {
                    icon: mdiCashMultiple,
                    text: {
                        el: `Ο πελάτης πρέπει να ξοδέψει τουλάχιστον ${this.couponWithTransaction.goal_minimum_amount}
                            ευρώ σε κάθε επίσκεψη`,
                        en: `
                            Customer must spend at least ${this.couponWithTransaction.goal_minimum_amount}
                            Euro on each visit
                        `,
                        it: `Il cliente deve spendere almeno ${this.couponWithTransaction.goal_minimum_amount}
                            euro in ogni visita`,
                    },
                },
                {
                    icon: mdiClockOutline,
                    text: {
                        el: `Κάθε επίσκεψη δεν μπορεί να απέχει περισσότερο από
                            ${this.couponWithTransaction.goal_max_days} μέρες από την τελευταία`,
                        en: `
                            The maximum distance between visits for the customer to be eligible for the coupon is 
                            ${this.couponWithTransaction.goal_max_days} days
                        `,
                        it: `
                            La distanza massima tra le visite affinché il cliente abbia diritto al coupon è di 
                            ${this.couponWithTransaction.goal_max_days} giorni`,
                    },
                },
            ];
        },

        headers() {
            return [
                { text: this.translations.user[this.lang], value: "mobile" },
                {
                    text: this.translations.totalVisits[this.lang],
                    value: "totalVisits",
                    align: "center",
                },
                {
                    text: this.translations.goalStatus[this.lang],
                    value: "status",
                    align: "center",
                },
            ];
        },
    },

    methods: {
        ...mapMutations(["setDeleteDialog", "setPermissionDialog"]),
        ...mapActions(["getPermission"]),
        ...mapActions("storePanel/coupons/couponsWithTransactions", [
            "getItemUsers",
            "remove",
        ]),
    },

    watch: {
        page(page) {
            this.getItemUsers({
                id: this.couponWithTransaction.coupon_id,
                query: `?page=${page}`,
            });
        },
    },
};
</script>
